// CompanyHistory.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useCompany } from "@/context/CompanyContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useManagementMode } from "@/hooks/useManagementMode";
import { SubscriptionGuard } from "@/components/auth/SubscriptionGuard";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { CompanySelector } from "@/components/company/CompanySelector";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { History, Plus, Edit, Trash2, Package, Search, ChevronDown, ChevronUp, User, Calendar, TrendingUp, ArrowRight, Camera } from "lucide-react";
import { CounterAnimation } from "@/components/common/CounterAnimation";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { BarcodeScanner } from "@/components/inventory/BarcodeScanner";

interface ActivityLog {
  id: string;
  action: string;
  entity_type: string;
  entity_name: string;
  details: any;
  created_at: string;
  profiles?: {
    full_name?: string;
    // email removed for security - no longer exposed to admins
  };
}

// Wrapper to avoid passing props to React.Fragment in instrumented environments
// Accepts any props but only renders children inside a fragment
// Prevents warnings like "Invalid prop supplied to React.Fragment"
type RowGroupProps = React.PropsWithChildren<Record<string, any>>;
const RowGroup: React.FC<RowGroupProps> = ({
  children
}) => <>{children}</>;

export const CompanyHistory: React.FC = () => {
  const {
    currentCompany
  } = useCompany();
  const {
    hasAccess,
    subscriptionData,
    isTrialActive,
    isSubscribedUser
  } = useSubscription();
  const { isCaisseMode, isRetraitMode } = useManagementMode();
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // When a barcode is scanned, update search and reset pagination
  const handleBarcodeScanned = (barcode: string) => {
    setSearchQuery(barcode);
    setCurrentPage(1);
  };
  
  // Fetch activities from Supabase for current company users
  useEffect(() => {
    if (currentCompany) {
      fetchActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);
  
  const allowPreTrial = !isTrialActive && !isSubscribedUser && (!subscriptionData || (!subscriptionData.is_trial && !subscriptionData.trial_end));
  
  const fetchActivities = async () => {
    if (!currentCompany) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("activity_logs")
        .select(`
            id,
            action,
            entity_type,
            entity_name,
            details,
            created_at,
            profiles:user_id (
              full_name
            )
          `)
        .eq("company_id", currentCompany.id)
        .order("created_at", { ascending: false })
        .limit(1000);

      if (error) throw error;
      setActivities((data || []) as ActivityLog[]);
    } catch (err) {
      console.error("Erreur fetch activities", err);
      toast({
        title: "Erreur",
        description: "Impossible de charger l'historique",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset page when filter or search changes (fix pagination/filter conflict)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, actionFilter]);

  // filtering (memoized)
  const isPageView = (activity: ActivityLog) => {
    const action = (activity.action || '').toLowerCase().replace(/\s+/g, '_');
    const type = (activity.entity_type || '').toLowerCase();
    return (
      action === 'page_view' ||
      action === 'pageview' ||
      action === 'view_page' ||
      action.includes('screen_view') ||
      action.includes('route_view') ||
      (type === 'page' && (action.includes('view') || action.includes('open')))
    );
  };

  const filteredActivities = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return activities.filter(activity => {
      if (isPageView(activity)) return false;
      // Email no longer accessible for security - search by name and entity only
      const fullName = (activity.profiles?.full_name || "").toLowerCase();
      const name = (activity.entity_name || "").toLowerCase();
      const d: any = activity.details || {};
      const u = d.updates || d.changes;
      const fromObj = d.old_values || d.previous || d.before || d.previous_values || d.from || d.old || d.prior || d.prev;
      const toBase = d.new_values || d.after || d.updated || d.next || d.to || d.new || d.current || d.payload || d.fields;
      const toObj = (toBase?.product || toBase?.data || toBase) ?? {};
      const candidates: any[] = [d.barcode, d.code_barre, d.code_barres, d.codebarre, d.ean, d.ean13, d.ean_13, d.sku, d.code, d.product?.barcode, d.product?.sku, d.product?.ean, d.data?.barcode, d.payload?.barcode, d.fields?.barcode, toObj?.barcode, fromObj?.barcode];
      if (u && u.barcode) {
        const b = u.barcode;
        if (b && typeof b === "object") {
          candidates.push(b.from, b.old, b.to, b.new, b.after, b.current, b.value, b.updated);
        } else {
          candidates.push(b);
        }
      }
      const barcodePool = candidates.filter(Boolean).map((v: any) => String(v).toLowerCase());
      const matchesBarcode = !!q && barcodePool.some((v: string) => v.includes(q));
      const matchesSearch = !q || name.includes(q) || fullName.includes(q) || matchesBarcode;
      const matchesAction = actionFilter === "all" || (activity.action || "").toLowerCase() === actionFilter;
      return matchesSearch && matchesAction;
    });
  }, [activities, searchQuery, actionFilter]);

  // pagination slice
  const totalPages = Math.max(1, Math.ceil(filteredActivities.length / rowsPerPage));
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentRows = filteredActivities.slice(indexOfFirst, indexOfLast);

  // stats
  const totalActions = activities.length;
  const creations = activities.filter(a => a.action.toLowerCase() === "create").length;
  const modifications = activities.filter(a => a.action.toLowerCase() === "update").length;
  const suppressions = activities.filter(a => a.action.toLowerCase() === "delete").length;

  // Identifier de façon fiable une vente
  const isSale = (a: ActivityLog) => {
    const action = (a.action || "").toLowerCase();
    const type = (a.entity_type || "").toLowerCase();
    return action === "vente" || action === "sale" || type === "vente" || type === "produit" && (a.details?.total_amount != null || a.details?.total_price != null || a.details?.total != null);
  };

  // Identifier de façon fiable un retrait
  const isWithdrawal = (a: ActivityLog) => {
    const action = (a.action || "").toLowerCase();
    const type = (a.entity_type || "").toLowerCase();
    return action === "retrait" || action === "withdrawal" || type === "retrait" || (a.details?.quantity_withdrawn != null);
  };

  const ventes = activities.filter(isSale).length;
  const retraits = activities.filter(isWithdrawal).length;
  const totalVentesMontant = activities.filter(isSale).reduce((sum, a) => {
    const raw = a.details?.total_amount ?? a.details?.total_price ?? a.details?.total ?? 0;
    const n = Number(raw);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);

  // utilities
  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString("fr-FR"),
      time: d.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit"
      })
    };
  };
  const formatPrice = (price: number) => new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR"
  }).format(price);
  const toggleExpand = (id: string) => setExpandedRows(prev => ({
    ...prev,
    [id]: !prev[id]
  }));
  const getActionIcon = (action: string) => {
    const a = action.toLowerCase();
    if (a === "create") return <Plus className="w-4 h-4 text-green-500" />;
    if (a === "update") return <Edit className="w-4 h-4 text-blue-500" />;
    if (a === "delete") return <Trash2 className="w-4 h-4 text-red-500" />;
    if (a === "vente" || a === "sale") return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (a === "retrait" || a === "withdrawal") return <Package className="w-4 h-4 text-orange-600" />;
    return <History className="w-4 h-4 text-gray-500" />;
  };

  // Fonction pour formater les labels des champs
  const labelOf = (key: string) => {
    switch (key) {
      case 'name':
      case 'product_name':
        return 'Nom du produit';
      case 'sku':
      case 'code':
        return 'Code / SKU';
      case 'barcode':
        return 'Code-barres';
      case 'serial_number':
        return 'Numéro de série';
      case 'price':
      case 'unit_price':
        return 'Prix unitaire';
      case 'cost_price':
        return 'Prix de revient';
      case 'quantity':
      case 'stock':
      case 'initial_stock':
        return 'Quantité / Stock initial';
      case 'min_threshold':
        return 'Seuil minimum';
      case 'category':
        return 'Catégorie';
      case 'description':
        return 'Description';
      case 'vat':
      case 'tax_rate':
        return 'TVA';
      case 'unit':
        return 'Unité';
      case 'active':
      case 'enabled':
        return 'Actif';
      case 'cycle_de_vie':
        return 'Cycle de vie';
      case 'status':
        return 'Statut';
      case 'payment_method':
        return 'Mode de paiement';
      case 'quantity_sold':
        return 'Quantité vendue';
      case 'total_amount':
        return 'Montant total';
      case 'role':
        return 'Rôle';
      case 'email':
        return 'Email';
      case 'full_name':
        return 'Nom complet';
      case 'company_id':
        return 'Entreprise';
      case 'user_id':
        return 'Utilisateur';
      case 'created_at':
        return 'Date de création';
      case 'updated_at':
        return 'Date de modification';
      default:
        return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
  };

  // Fonction pour formater les valeurs selon leur type
  const formatValue = (key: string, value: any) => {
    if (value === null || value === undefined || value === '') return '—';
    const k = String(key).toLowerCase();
    
    // Prix et montants
    if (k === 'price' || k === 'unit_price' || k === 'cost_price' || k === 'total_amount') {
      const n = Number(value);
      return isNaN(n) ? String(value) : formatPrice(n);
    }
    
    // Quantités et nombres
    if (k === 'quantity' || k === 'stock' || k === 'initial_stock' || k === 'min_threshold' || k === 'quantity_sold') {
      const n = Number(value);
      return isNaN(n) ? String(value) : `${n} unité${n > 1 ? 's' : ''}`;
    }
    
    // Pourcentages
    if (k === 'vat' || k === 'tax_rate') {
      const n = Number(value);
      return isNaN(n) ? String(value) : `${n}%`;
    }
    
    // Booléens
    if (k === 'active' || k === 'enabled') {
      return Boolean(value) ? 'Oui' : 'Non';
    }
    
    // Cycle de vie des produits
    if (k === 'cycle_de_vie') {
      const lifecycleLabels: { [key: string]: string } = {
        'best_seller': 'Best-seller',
        'en_promotion': 'En promotion',
        'fin_de_serie': 'Fin de série',
        'archive': 'Archivé'
      };
      return lifecycleLabels[String(value)] || String(value);
    }
    
    // Modes de paiement
    if (k === 'payment_method') {
      const paymentLabels: { [key: string]: string } = {
        'Espèces': 'Espèces',
        'Carte bancaire': 'Carte bancaire',
        'Chèque': 'Chèque',
        'Virement': 'Virement',
        'Autre': 'Autre'
      };
      return paymentLabels[String(value)] || String(value);
    }
    
    // Rôles
    if (k === 'role') {
      const roleLabels: { [key: string]: string } = {
        'admin': 'Administrateur',
        'member': 'Membre',
        'user': 'Utilisateur',
        'owner': 'Propriétaire'
      };
      return roleLabels[String(value)] || String(value);
    }
    
    // Statuts
    if (k === 'status') {
      const statusLabels: { [key: string]: string } = {
        'pending': 'En attente',
        'accepted': 'Accepté',
        'rejected': 'Rejeté',
        'active': 'Actif',
        'inactive': 'Inactif'
      };
      return statusLabels[String(value)] || String(value);
    }
    
    // Dates
    if (k.endsWith('_at') || k.includes('date') || k === 'created_at' || k === 'updated_at') {
      const d = new Date(String(value));
      return isNaN(d.getTime()) ? String(value) : d.toLocaleString('fr-FR');
    }
    
    // Objets JSON - les formatter de manière lisible
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'Aucun élément';
      }
      // Pour les objets, essayer d'extraire les valeurs importantes
      if (value && typeof value === 'object') {
        const keys = Object.keys(value);
        if (keys.length === 0) return 'Objet vide';
        if (keys.length <= 3) {
          return keys.map(k => `${k}: ${value[k]}`).join(', ');
        }
        return `Objet (${keys.length} propriétés)`;
      }
      return JSON.stringify(value);
    }
    
    return String(value);
  };

  // Fonction pour afficher les détails de modification avec diff
  const renderModificationDetails = (activity: ActivityLog) => {
    const details: any = activity.details || {};
    const updates = details?.updates || details?.changes;
    const oldValues = details?.old_values;
    const changes = details?.changes;
    
    const hideKey = (k: string) => /(^id$|_id$|company_id$|user_id$|product_id$)/i.test(k);

    if (updates && Object.keys(updates).length > 0) {
      const fieldsToShow = Object.entries(updates)
        .filter(([key]) => !hideKey(key))
        .map(([key, value]) => {
          const label = labelOf(key);
          
          // Récupérer les anciennes valeurs depuis les différentes sources
          let oldValue = undefined;
          if (changes && changes[key]) {
            oldValue = changes[key].from;
          } else if (oldValues && oldValues[key] !== undefined) {
            oldValue = oldValues[key];
          }
          
          return { key, value, oldValue, label };
        })
        .filter(({ oldValue, value }) => {
          // Ne garder que les champs qui ont réellement changé
          if (oldValue === undefined || oldValue === null) {
            return value !== undefined && value !== null && value !== '';
          }
          return JSON.stringify(oldValue) !== JSON.stringify(value);
        });

      if (fieldsToShow.length === 0) {
        return <div className="text-sm text-muted-foreground">Aucune modification détectée</div>;
      }

      return (
        <div className="space-y-3">
          {fieldsToShow.map(({ key, value, oldValue, label }) => {
            const hasOldValue = oldValue !== undefined && oldValue !== null;
            
            return (
              <div key={key} className="border border-border/50 rounded-lg p-3 bg-background/50">
                <div className="font-medium text-sm mb-3 text-foreground">{label}</div>
                
                {hasOldValue ? (
                  // Affichage avec anciennes et nouvelles valeurs
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-red-600 dark:text-red-400 flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        Ancienne valeur
                      </div>
                      <div className="p-2 rounded border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30 text-sm">
                        {formatValue(key, oldValue)}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        Nouvelle valeur
                      </div>
                      <div className="p-2 rounded border border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950/30 text-sm">
                        {formatValue(key, value)}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Affichage simple pour les nouvelles valeurs (création)
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      Valeur ajoutée
                    </div>
                    <div className="p-3 rounded border border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/30 text-sm">
                      {formatValue(key, value)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return <div className="text-sm text-muted-foreground">Aucune donnée de modification disponible</div>;
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        
        <div className="flex-1 flex flex-col">
          {/* Header unifié */}
          <header className="border-b bg-background/95 text-foreground backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
            <div className="flex h-14 sm:h-16 items-center gap-2 sm:gap-4 px-3 sm:px-4">
              <SidebarTrigger />
              
              <div className="flex-1 flex items-center justify-between">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0">
                  <h1 className="text-lg sm:text-xl font-semibold truncate">
                    Historique de l'entreprise {currentCompany && `- ${currentCompany.name}`}
                  </h1>
                  <div className="hidden sm:block">
                    <CompanySelector />
                  </div>
                </div>
                
                <div className="flex items-center gap-2 sm:gap-4">
                </div>
              </div>
            </div>
          </header>

          {/* Contenu principal */}
          <main className={`flex-1 p-3 sm:p-4 lg:p-6 ${!hasAccess ? 'pb-24' : ''}`}>
            <div className="max-w-7xl mx-auto space-y-6">
              {/* En-tête de la page */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
                    <History className="w-8 h-8 text-primary" />
                    Historique de l'entreprise
                  </h1>
                  <p className="text-muted-foreground mt-2">
                    Consultez toutes les actions réalisées par votre équipe
                  </p>
                </div>
              </div>

              {/* Contrôles et recherche */}
              <div className="space-y-6">
                  {/* Statistiques */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <Card className="group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 border-border/50 animate-fade-in cursor-pointer">
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/10 to-green-600/20 transition-transform duration-200 hover:scale-105">
                            <Plus className="w-6 h-6 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium mb-1 text-green-700 dark:text-green-300">
                              Créations
                            </p>
                            <p className="text-3xl font-bold text-green-800 dark:text-green-200">
                              <CounterAnimation value={creations} />
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              Créations
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>

                    <Card className="group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 border-border/50 animate-fade-in cursor-pointer" style={{animationDelay: '0.1s'}}>
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-600/20 transition-transform duration-200 hover:scale-105">
                            <Edit className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium mb-1 text-blue-700 dark:text-blue-300">
                              Modifications
                            </p>
                            <p className="text-3xl font-bold text-blue-800 dark:text-blue-200">
                              <CounterAnimation value={modifications} />
                            </p>
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-2">
                              Suppression
                              <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                                <Trash2 className="w-3 h-3" />
                                {suppressions}
                              </span>
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>

                    {isCaisseMode && (
                      <Card className="group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 border-border/50 animate-fade-in cursor-pointer" style={{animationDelay: '0.3s'}}>
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-600/20 transition-transform duration-200 hover:scale-105">
                              <Package className="w-6 h-6 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium mb-1 text-purple-700 dark:text-purple-300">
                                Ventes
                              </p>
                              <p className="text-3xl font-bold text-purple-800 dark:text-purple-200">
                                <CounterAnimation value={ventes} />
                              </p>
                              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                Ventes - {totalVentesMontant > 0 ? formatPrice(totalVentesMontant) : 'Aucune vente'}
                              </p>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    )}

                    {isRetraitMode && (
                      <Card className="group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 border-border/50 animate-fade-in cursor-pointer" style={{animationDelay: '0.4s'}}>
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            <div className="p-3 rounded-lg bg-gradient-to-br from-orange-500/10 to-orange-600/20 transition-transform duration-200 hover:scale-105">
                              <Package className="w-6 h-6 text-orange-600" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium mb-1 text-orange-700 dark:text-orange-300">
                                Retraits
                              </p>
                              <p className="text-3xl font-bold text-orange-800 dark:text-orange-200">
                                <CounterAnimation value={retraits} />
                              </p>
                              <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                Retraits de stock
                              </p>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    )}
                  </div>

                  {/* Contrôles de recherche et filtrage */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Rechercher par nom, email, produit, code-barres..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    <Select value={actionFilter} onValueChange={setActionFilter}>
                      <SelectTrigger className="w-full sm:w-[180px]">
                        <SelectValue placeholder="Filtrer par action" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les actions</SelectItem>
                        <SelectItem value="create">Créations</SelectItem>
                        <SelectItem value="update">Modifications</SelectItem>
                        <SelectItem value="delete">Suppressions</SelectItem>
                        <SelectItem value="vente">Ventes</SelectItem>
                        <SelectItem value="retrait">Retraits</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Table des activités */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-600/20 transition-transform duration-200 hover:scale-105">
                          <History className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium mb-1 text-purple-700 dark:text-purple-300">
                            Journal d'activité
                          </p>
                          <p className="text-xl font-bold text-purple-800 dark:text-purple-200">
                            Journal d'activité
                            {filteredActivities.length !== activities.length && (
                              <>
                                {" "}
                                <Badge variant="secondary" className="ml-2">
                                  {filteredActivities.length} sur {activities.length}
                                </Badge>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div className="flex items-center justify-center p-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      ) : currentRows.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p className="text-lg font-medium mb-2">Aucune activité trouvée</p>
                          <p className="text-sm">
                            {searchQuery || actionFilter !== "all"
                              ? "Aucun résultat ne correspond à vos critères de recherche."
                              : "Les activités de votre équipe apparaîtront ici."}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Container avec scroll horizontal responsive */}
                          <div className="overflow-x-auto -mx-4 sm:-mx-6">
                            <div className="min-w-[600px] px-4 sm:px-6">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-[100px] min-w-[100px]">Date</TableHead>
                                    <TableHead className="w-[80px] min-w-[80px]">Action</TableHead>
                                    <TableHead className="min-w-[200px]">Élément</TableHead>
                                    <TableHead className="w-[150px] min-w-[150px]">Utilisateur</TableHead>
                                    <TableHead className="w-[50px] min-w-[50px]"></TableHead>
                                  </TableRow>
                                </TableHeader>
                              <TableBody>
                                {currentRows.map((activity) => (
                                  <RowGroup key={activity.id}>
                                     <TableRow 
                                       className="cursor-pointer hover:bg-gradient-to-r hover:from-muted/30 hover:to-muted/60 transition-all duration-300 animate-fade-in"
                                       onClick={() => toggleExpand(activity.id)}
                                     >
                                      <TableCell>
                                        <div className="text-sm">
                                          <div className="font-medium">{formatDate(activity.created_at).date}</div>
                                          <div className="text-muted-foreground">{formatDate(activity.created_at).time}</div>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          {getActionIcon(activity.action)}
                                          <span className="text-sm font-medium capitalize">
                                            {activity.action.toLowerCase()}
                                          </span>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="max-w-[300px]">
                                          <div className="font-medium truncate">{activity.entity_name}</div>
                                          <div className="text-sm text-muted-foreground capitalize">
                                            {activity.entity_type}
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                         <div className="flex items-center gap-2">
                                           <User className="w-4 h-4 text-muted-foreground" />
                                           <div className="text-sm">
                                             <div className="font-medium">
                                               {activity.profiles?.full_name || "Utilisateur"}
                                             </div>
                                             <div className="text-muted-foreground text-xs">
                                               Membre de l'équipe
                                             </div>
                                           </div>
                                         </div>
                                      </TableCell>
                                      <TableCell>
                                        {expandedRows[activity.id] ? (
                                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                        ) : (
                                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                        )}
                                      </TableCell>
                                    </TableRow>

                                    {/* Ligne de détails expandable */}
                                    {expandedRows[activity.id] && (
                                      <TableRow>
                                        <TableCell colSpan={5} className="bg-muted/30">
                                          <div className="p-4 space-y-3">
                                            <div className="flex items-center gap-2 text-sm font-medium">
                                              <Calendar className="w-4 h-4" />
                                              Détails de l'action
                                            </div>
                                            
                                            <div className="text-sm text-muted-foreground bg-background/50 p-3 rounded-md border-l-4 border-primary/20">
                                              {activity.details ? (
                                                <div className="space-y-3">
                                                  {activity.action.toLowerCase() === 'update' ? (
                                                    <div className="space-y-3">
                                                      <div className="font-medium text-foreground mb-3 flex items-center gap-2">
                                                        <Edit className="w-4 h-4" />
                                                        Modifications effectuées
                                                      </div>
                                                      {renderModificationDetails(activity)}
                                                    </div>
                                                   ) : activity.action.toLowerCase() === 'retrait' ? (
                                                     <div className="space-y-3">
                                                       <div className="font-medium text-foreground mb-3 flex items-center gap-2">
                                                         <Package className="w-4 h-4" />
                                                         Détails du retrait
                                                       </div>
                                                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                         {activity.details?.quantity_withdrawn && (
                                                           <div className="flex items-start gap-3 py-2 border-b border-border/50">
                                                             <span className="font-medium text-foreground min-w-0 flex-shrink-0 text-sm">
                                                               Quantité retirée :
                                                             </span>
                                                             <span className="text-red-600 font-semibold text-sm">
                                                               -{activity.details.quantity_withdrawn}
                                                             </span>
                                                           </div>
                                                         )}
                                                         {activity.details?.reason && (
                                                           <div className="flex items-start gap-3 py-2 border-b border-border/50">
                                                             <span className="font-medium text-foreground min-w-0 flex-shrink-0 text-sm">
                                                               Raison :
                                                             </span>
                                                             <span className="text-muted-foreground text-sm">
                                                               {activity.details.reason}
                                                             </span>
                                                           </div>
                                                         )}
                                                       </div>
                                                     </div>
                                                   ) : (
                                                    <div className="space-y-2">
                                                      {Object.entries(activity.details).map(([key, value]) => {
                                                        if (key === 'updates' || key === 'changes' || 
                                                            key === 'old_values' || key === 'new_values' || 
                                                            key === 'before' || key === 'after' ||
                                                            /(^id$|_id$|company_id$|user_id$|product_id$)/i.test(key)) {
                                                          return null;
                                                        }
                                                        return (
                                                          <div key={key} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0">
                                                            <span className="font-medium text-foreground min-w-0 flex-shrink-0 text-sm">
                                                              {labelOf(key)} :
                                                            </span>
                                                            <span className="text-muted-foreground text-sm break-words">
                                                              {formatValue(key, value)}
                                                            </span>
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  )}
                                                </div>
                                              ) : (
                                                <p>Aucun détail disponible pour cette action.</p>
                                              )}
                                            </div>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </RowGroup>
                                ))}
                              </TableBody>
                              </Table>
                            </div>
                          </div>

                          {/* Pagination */}
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">Lignes par page :</span>
                              <Select
                                value={rowsPerPage.toString()}
                                onValueChange={(value) => {
                                  setRowsPerPage(Number(value));
                                  setCurrentPage(1);
                                }}
                              >
                                <SelectTrigger className="w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="10">10</SelectItem>
                                  <SelectItem value="25">25</SelectItem>
                                  <SelectItem value="50">50</SelectItem>
                                  <SelectItem value="100">100</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                                disabled={currentPage === 1}
                              >
                                Précédent
                              </Button>
                              
                              <div className="flex items-center gap-2 text-sm">
                                <span>Page</span>
                                <span className="font-medium">{currentPage}</span>
                                <span>sur</span>
                                <span className="font-medium">{totalPages}</span>
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                                disabled={currentPage === totalPages}
                              >
                                Suivant
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  };